import { MapPolygon } from '@/components/maps';
import { importCandidateStyle } from '@/lib/maps/boundary-style';

import type { ImportedShape } from '../schemas';

export type ImportCandidatesProps = {
  /** Every **Imported shape** the file held, in the server's order. **Never filtered** — see the
   *  component note below. */
  shapes: readonly ImportedShape[];
  /** The chosen shape's `index`, or `null` when the Grower has not chosen yet. */
  selectedIndex: number | null;
  /** A click on any candidate, selectable or not. The page decides what that means, because the
   *  two answers it can give — select, or say why not — are a selection write and a toast, and
   *  both belong with the state and the toaster rather than here. */
  onPick: (shape: ImportedShape) => void;
};

/**
 * The **Import preview**'s candidate layer — Figma `1657:790446` (nothing selected) and
 * `1657:790982` (one selected).
 *
 * **Every shape the file held is drawn**, including the ones the file put out of reach, because a
 * boundary plainly present in the Grower's file and silently absent from the screen reads as data
 * loss — the BE's own argument: *"Showing nothing where a boundary plainly exists reads as data
 * loss; showing it with a reason reads as an explanation."*
 *
 * **`kind` is not read and no shape is filtered** — `shapes` is mapped whole, and an **Interior
 * shape** appears as an ordinary candidate with no badge, label or nesting affordance. The argument
 * is recorded once, where the decision is enacted: `importedShapeSchema`'s note on leaving `kind`
 * unmodelled (`../schemas.ts`).
 *
 * `interactive` is **true for every candidate, unselectable ones included**: a shape that cannot be
 * chosen still has to explain itself when clicked, and `<MapPolygon>` only wires `onClick` while
 * interactive. It is also what supplies the pointer cursor, which on this surface is the only hover
 * affordance a selectable candidate gets — `DRAFT_BOUNDARY`'s `fillOpacityHover` equals its
 * `fillOpacity`, and deepening it would be inventing a state no frame draws.
 *
 * **No draw-order sort.** Figma `1657:790982` leaves the selected Vector 6 in file order between
 * Vectors 5 and 7 and it still reads plainly, because `IMPORT_SELECTED` sits at `fillOpacity: 0.6`
 * against `DRAFT_BOUNDARY`'s `0.3`.
 *
 * One ring per candidate: an **Imported shape** carries no interior ring of its own.
 *
 * Its own component rather than JSX in the page for the constraint the feature's grilling placed on
 * this whole surface — `<MapPage>` reached 933 lines and this page must not go the same way — and
 * because it is the seam ticket 05 wraps in its Placement-mode branch.
 */
export function ImportCandidates({ shapes, selectedIndex, onPick }: ImportCandidatesProps) {
  return (
    <>
      {shapes.map((shape) => (
        <MapPolygon
          // `index` is the server's stable handle, NOT the array position — a shape the tolerant
          // parse dropped renumbers the position and not this.
          key={shape.index}
          rings={[shape.points]}
          bounds={shape.bounds}
          style={importCandidateStyle({
            selectable: shape.selectable,
            selected: shape.index === selectedIndex,
          })}
          interactive
          onClick={() => onPick(shape)}
        />
      ))}
    </>
  );
}
